import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import { useParams, useNavigate } from 'react-router-dom';
import { Photo } from '../types/Photo';
import { getAllPhotos } from '../services/photoService';
import ProtectedImage from './ProtectedImage';
import { H1, H2, H3, H5, H6, P, PSmall } from './Typography';

const PageContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
  overflow: hidden;

  @media (max-width: 768px) {
    height: auto;
    min-height: 100vh;
    overflow-y: auto;
    overflow-x: hidden;
    -webkit-overflow-scrolling: touch;
  }
`;

const BookContainer = styled.div`
  width: 100%;
  height: 100vh;
  display: flex;
  gap: 30px;
  align-items: flex-end;
  padding: 40px 100px;
  box-sizing: border-box;
  position: relative;

  @media (max-width: 1024px) {
    flex-direction: column;
    gap: 40px;
    padding: 40px;
  }

  @media (max-width: 768px) {
    height: auto;
    min-height: 100vh;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    padding: 80px 24px 40px;
    gap: 32px;
  }
`;

const PhotoContainer = styled.div<{ $isTransitioning?: boolean; $width?: number; $height?: number }>`
  display: flex;
  width: ${(props) => (props.$width && props.$isTransitioning ? `${props.$width}px` : 'fit-content')};
  height: ${(props) => (props.$height && props.$isTransitioning ? `${props.$height}px` : 'auto')};
  max-width: 60vw;
  opacity: ${(props) => (props.$isTransitioning ? 0 : 1)};
  transition: opacity 0.8s ease-in-out;
  cursor: zoom-in;
  justify-content: center;
  align-items: center;

  @media (max-width: 768px) {
    max-width: 100%;
    width: 100%;
    cursor: default;
  }
`;

const StyledProtectedImage = styled(ProtectedImage)`
  max-width: 60vw;
  max-height: calc(100vh - 80px);
  width: auto;
  height: auto;

  img {
    max-width: 60vw;
    max-height: calc(100vh - 80px);
    width: auto;
    height: auto;
    object-fit: contain;
  }

  @media (max-width: 768px) {
    max-width: 100%;
    max-height: none;
    width: 100%;

    img {
      max-width: 100%;
      max-height: none;
      width: 100%;
      height: auto;
      object-fit: contain;
    }
  }
`;

const TextContainer = styled.div<{ $isTransitioning?: boolean }>`
  flex: 0 0 400px;
  display: flex;
  flex-direction: column;
  gap: 15px;
  padding-bottom: 20px;
  opacity: ${(props) => (props.$isTransitioning ? 0 : 1)};
  transition: opacity 0.8s ease-in-out;

  @media (max-width: 1024px) {
    flex: 1;
    width: 100%;
  }

  @media (max-width: 768px) {
    flex: none;
    width: 100%;
    padding-bottom: 0;
  }
`;

const Title = styled(H5)`
  margin: 0;
  line-height: 1.2;
`;

const Location = styled(P)`
  color: #666;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 18px;

  &::before {
    content: '📍';
  }
`;

const DescriptionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Description = styled(PSmall)`
  line-height: 1.8;
  margin: 0;
`;

const MetaInfo = styled(PSmall)`
  color: #999;
  font-size: 13px;
  margin-top: 8px;
`;

const DateText = styled(P)`
  color: #999;
  font-size: 14px;
`;

const CameraSection = styled.div`
  margin-top: 16px;
  padding-top: 24px;
  border-top: 1px solid #e0e0e0;
`;

const CameraTitle = styled(H2)`
  font-size: 18px;
  margin-bottom: 16px;
  color: #333;
`;

const CameraGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
`;

const CameraDetail = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const CameraLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  color: #999;
  letter-spacing: 0.5px;
`;

const CameraValue = styled.span`
  font-size: 14px;
  color: #333;
  font-weight: 500;
`;

const NavigationContainer = styled.div<{ $visible: boolean }>`
  position: absolute;
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
  opacity: ${(props) => (props.$visible ? 1 : 0)};
  transition: opacity 0.3s ease;
  pointer-events: ${(props) => (props.$visible ? 'auto' : 'none')};

  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileHomeButton = styled.button`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    position: fixed;
    top: 20px;
    left: 20px;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    border: 1.5px solid #333;
    background: white;
    color: #333;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;
    font-size: 20px;
    z-index: 100;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

    &:hover:not(:disabled) {
      background: #333;
      color: white;
      transform: scale(1.05);
    }

    &:active:not(:disabled) {
      transform: scale(0.95);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
`;

const NavButton = styled.button<{ disabled?: boolean }>`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 1px solid ${(props) => (props.disabled ? '#e0e0e0' : '#333')};
  background: white;
  color: ${(props) => (props.disabled ? '#ccc' : '#333')};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.2s ease;
  font-size: 14px;

  &:hover:not(:disabled) {
    background: #333;
    color: white;
    transform: scale(1.05);
  }

  &:active:not(:disabled) {
    transform: scale(0.95);
  }

  @media (max-width: 768px) {
    width: 26px;
    height: 26px;
    font-size: 12px;
  }
`;

const PageIndicator = styled.div`
  font-size: 11px;
  color: #666;
  font-weight: 500;
  text-align: center;
  padding: 3px 0;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  font-size: 18px;
  color: #666;
`;

const ErrorContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  font-size: 18px;
  color: #d32f2f;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  text-align: center;
  padding: 40px 20px;
  color: #666;

  h2 {
    font-size: 24px;
    margin-bottom: 12px;
  }

  p {
    font-size: 16px;
    color: #999;
  }
`;

const FullscreenOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(40, 40, 40, 0.98);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
`;

const FullscreenImageContainer = styled.div<{ $isZoomed: boolean }>`
  position: relative;
  width: 95vw;
  height: 95vh;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: ${(props) => (props.$isZoomed ? 'auto' : 'hidden')};
  cursor: ${(props) => (props.$isZoomed ? 'zoom-out' : 'zoom-in')};
`;

const FullscreenImageWrapper = styled.div<{ $zoomLevel: number }>`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: ${(props) => (props.$zoomLevel > 1 ? `${props.$zoomLevel * 100}%` : 'auto')};
  min-height: ${(props) => (props.$zoomLevel > 1 ? `${props.$zoomLevel * 100}%` : 'auto')};
`;

const FullscreenImage = styled.img<{ $zoomLevel: number }>`
  max-width: ${(props) => (props.$zoomLevel > 1 ? 'none' : '95vw')};
  max-height: ${(props) => (props.$zoomLevel > 1 ? 'none' : '95vh')};
  width: ${(props) => (props.$zoomLevel > 1 ? `${props.$zoomLevel * 95}vw` : 'auto')};
  height: auto;
  object-fit: contain;
  user-select: none;
  pointer-events: none;
`;

const CloseButton = styled.button`
  position: fixed;
  top: 20px;
  right: 20px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 1.5px solid rgba(255, 255, 255, 0.7);
  background: rgba(0, 0, 0, 0.4);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 18px;
  z-index: 10000;

  &:hover {
    background: rgba(0, 0, 0, 0.7);
    border-color: white;
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }

  @media (max-width: 768px) {
    top: 12px;
    right: 12px;
    width: 32px;
    height: 32px;
    font-size: 16px;
  }
`;

const GalleryOverlay = styled.div<{ $isTransitioning?: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.95);
  z-index: 9999;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 80px 40px 40px;
  box-sizing: border-box;
  opacity: ${(props) => (props.$isTransitioning ? 0 : 1)};
  transition: opacity 0.8s ease-in-out;
`;

const GalleryGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  width: 100%;
  max-width: 1600px;
  margin: 0 auto;
  justify-content: center;
  align-items: flex-start;

  @media (max-width: 768px) {
    gap: 16px;
  }
`;

const GalleryImageContainer = styled.div<{ $disabled?: boolean }>`
  position: relative;
  height: 400px;
  border-radius: 4px;
  cursor: ${(props) => (props.$disabled ? 'not-allowed' : 'pointer')};
  transition: transform 0.2s ease;
  overflow: hidden;
  pointer-events: ${(props) => (props.$disabled ? 'none' : 'auto')};

  &:hover {
    transform: ${(props) => (props.$disabled ? 'none' : 'scale(1.05)')};
  }

  @media (max-width: 768px) {
    height: 280px;
  }
`;

const GalleryImage = styled(ProtectedImage)`
  height: 100%;
  width: auto;

  img {
    height: 100%;
    width: auto;
    object-fit: contain;
    display: block;
  }
`;

const GalleryButton = styled(NavButton)`
  font-size: 14px;

  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

const PortfolioName = styled.div<{ $isTransitioning?: boolean }>`
  position: absolute;
  top: 20px;
  right: 20px;
  font-family: 'Inter', sans-serif;
  font-size: 18px;
  font-weight: 600;
  color: #333;
  cursor: pointer;
  opacity: ${(props) => (props.$isTransitioning ? 0 : 1)};
  transition: opacity 0.8s ease-in-out, color 0.2s ease;
  letter-spacing: -0.015em;
  user-select: none;
  z-index: 100;

  &:hover {
    color: #666;
  }

  @media (max-width: 1024px) {
    top: 20px;
    right: 40px;
    font-size: 16px;
  }

  @media (max-width: 768px) {
    top: 16px;
    right: 24px;
    font-size: 14px;
  }
`;

const PhotoBook: React.FC = () => {
  const { index } = useParams<{ index?: string }>();
  const navigate = useNavigate();
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isTransitioning, setIsTransitioning] = useState<boolean>(true); // Start true for initial fade-in
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [zoomLevel, setZoomLevel] = useState<number>(1);
  const [showNavigation, setShowNavigation] = useState<boolean>(true);
  const [showGallery, setShowGallery] = useState<boolean>(false);
  const [isGalleryTransitioning, setIsGalleryTransitioning] = useState<boolean>(false);
  const [isHoveringNav, setIsHoveringNav] = useState<boolean>(false);
  const [containerDimensions, setContainerDimensions] = useState<{ width: number; height: number } | null>(null);
  const fullscreenRef = React.useRef<HTMLDivElement>(null);
  const hideNavTimeout = React.useRef<NodeJS.Timeout | null>(null);
  const photoContainerRef = React.useRef<HTMLDivElement>(null);
  const isInitialMount = React.useRef<boolean>(true);

  // Redirect to home on mobile since all photos are in one scroll
  useEffect(() => {
    if (window.innerWidth <= 768) {
      navigate('/', { replace: true });
    }
  }, [navigate]);

  // Preload image utility (defined early for use in effects)
  const preloadImage = useCallback((imageUrl: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve();
      img.onerror = () => reject();
      img.src = imageUrl;
    });
  }, []);

  useEffect(() => {
    const fetchPhotos = async () => {
      try {
        setLoading(true);
        const fetchedPhotos = await getAllPhotos();
        setPhotos(fetchedPhotos);
        setError(null);
      } catch (err) {
        setError('Failed to load photos. Please try again later.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchPhotos();
  }, []);

  // Update current index when URL param changes
  useEffect(() => {
    // If no photos yet, wait
    if (photos.length === 0) return;

    if (index !== undefined) {
      const newIndex = parseInt(index, 10);

      // Redirect to landing page if trying to access index 0
      if (newIndex === 0) {
        navigate('/', { replace: true });
        return;
      }

      if (!isNaN(newIndex) && newIndex >= 1 && newIndex < photos.length) {
        // Handle index change
        if (currentIndex !== newIndex || isInitialMount.current) {
          if (isInitialMount.current) {
            isInitialMount.current = false;
          }

          setIsTransitioning(true);
          setCurrentIndex(newIndex);

          // Preload the image first
          const photo = photos[newIndex];
          if (photo) {
            preloadImage(photo.imageURL).then(() => {
              // Fade in after image is loaded
              setTimeout(() => {
                setIsTransitioning(false);
                setContainerDimensions(null);
              }, 50);
            }).catch(() => {
              // Fade in even if preload fails
              setTimeout(() => {
                setIsTransitioning(false);
                setContainerDimensions(null);
              }, 100);
            });
          }
        }
      } else if (!isNaN(newIndex) && newIndex >= photos.length) {
        // If index is out of bounds, redirect to last photo
        navigate(`/book/${photos.length - 1}`, { replace: true });
      } else if (isNaN(newIndex) || newIndex < 0) {
        // Invalid index, redirect to first photobook photo
        navigate('/book/1', { replace: true });
      }
    } else {
      // No index parameter, redirect to first photobook photo
      navigate('/book/1', { replace: true });
    }
  }, [index, photos, currentIndex, navigate, preloadImage]);

  // Preload adjacent images for smoother transitions
  useEffect(() => {
    if (photos.length === 0 || isTransitioning) return;

    const preloadAdjacent = () => {
      // Preload next image
      if (currentIndex < photos.length - 1) {
        const nextPhoto = photos[currentIndex + 1];
        if (nextPhoto) {
          preloadImage(nextPhoto.imageURL).catch(() => {
            // Ignore preload errors
          });
        }
      }

      // Preload previous image
      if (currentIndex > 1) {
        const prevPhoto = photos[currentIndex - 1];
        if (prevPhoto) {
          preloadImage(prevPhoto.imageURL).catch(() => {
            // Ignore preload errors
          });
        }
      }

      // If at index 1, preload landing page image (index 0)
      if (currentIndex === 1) {
        const landingPhoto = photos[0];
        if (landingPhoto) {
          preloadImage(landingPhoto.imageURL).catch(() => {
            // Ignore preload errors
          });
        }
      }
    };

    preloadAdjacent();
  }, [currentIndex, photos, isTransitioning, preloadImage]);

  const handlePrevious = useCallback(() => {
    if (isTransitioning || currentIndex < 1) return;

    // Capture current container dimensions
    if (photoContainerRef.current) {
      const { width, height } = photoContainerRef.current.getBoundingClientRect();
      setContainerDimensions({ width, height });
    }

    setIsTransitioning(true);

    // If at index 1, go to landing page
    if (currentIndex === 1) {
      // Preload the landing page image (index 0)
      const landingPhoto = photos[0];
      if (landingPhoto) {
        const preloadPromise = preloadImage(landingPhoto.imageURL);

        // Wait for fade out to complete (800ms)
        setTimeout(() => {
          // Navigate immediately
          navigate('/');

          // Wait for image to be loaded before fading in
          preloadPromise.then(() => {
            setTimeout(() => {
              setIsTransitioning(false);
              setContainerDimensions(null);
            }, 50);
          }).catch(() => {
            // Even if preload fails, fade in after a short delay
            setTimeout(() => {
              setIsTransitioning(false);
              setContainerDimensions(null);
            }, 100);
          });
        }, 800);
      }
    } else {
      // Going back within photobook (e.g., from index 3 to index 2)
      const newIndex = currentIndex - 1;
      const nextPhoto = photos[newIndex];
      if (nextPhoto) {
        const preloadPromise = preloadImage(nextPhoto.imageURL);

        // Wait for fade out to complete (800ms)
        setTimeout(() => {
          // Navigate immediately
          navigate(`/book/${newIndex}`);

          // Wait for image to be loaded before fading in
          preloadPromise.then(() => {
            setTimeout(() => {
              setIsTransitioning(false);
              setContainerDimensions(null);
            }, 50);
          }).catch(() => {
            // Even if preload fails, fade in after a short delay
            setTimeout(() => {
              setIsTransitioning(false);
              setContainerDimensions(null);
            }, 100);
          });
        }, 800);
      }
    }
  }, [isTransitioning, currentIndex, navigate, photos, preloadImage]);

  const handleNext = useCallback(() => {
    if (isTransitioning || currentIndex === photos.length - 1) return;

    // Capture current container dimensions
    if (photoContainerRef.current) {
      const { width, height } = photoContainerRef.current.getBoundingClientRect();
      setContainerDimensions({ width, height });
    }

    setIsTransitioning(true);
    const newIndex = Math.min(photos.length - 1, currentIndex + 1);

    // Start preloading the new image immediately
    const nextPhoto = photos[newIndex];
    if (nextPhoto) {
      const preloadPromise = preloadImage(nextPhoto.imageURL);

      // Wait for fade out to complete (800ms)
      setTimeout(() => {
        // Navigate immediately
        navigate(`/book/${newIndex}`);

        // Wait for image to be loaded before fading in
        preloadPromise.then(() => {
          setTimeout(() => {
            setIsTransitioning(false);
            setContainerDimensions(null);
          }, 50);
        }).catch(() => {
          // Even if preload fails, fade in after a short delay
          setTimeout(() => {
            setIsTransitioning(false);
            setContainerDimensions(null);
          }, 100);
        });
      }, 800);
    }
  }, [isTransitioning, currentIndex, photos, navigate, preloadImage]);

  const enterFullscreen = useCallback(() => {
    // Disable fullscreen on mobile
    if (window.innerWidth <= 768) return;

    setIsFullscreen(true);
    if (fullscreenRef.current) {
      fullscreenRef.current.requestFullscreen();
    }
  }, []);

  const exitFullscreen = useCallback(() => {
    setIsFullscreen(false);
    setZoomLevel(1);
    if (document.fullscreenElement) {
      document.exitFullscreen();
    }
  }, []);

  const toggleZoom = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setZoomLevel((prev) => (prev === 1 ? 2 : 1));
  }, []);

  const openGallery = useCallback(() => {
    if (isGalleryTransitioning) return;

    // Capture current container dimensions
    if (photoContainerRef.current) {
      const { width, height } = photoContainerRef.current.getBoundingClientRect();
      setContainerDimensions({ width, height });
    }

    setIsTransitioning(true);

    // Preload gallery images (slice(1) to exclude landing page)
    const preloadPromises = photos.slice(1).map(photo => preloadImage(photo.imageURL));

    // Wait for fade out to complete (800ms)
    setTimeout(() => {
      // Show gallery with transition state true (starts faded out)
      setIsGalleryTransitioning(true);
      setShowGallery(true);

      // Wait for images to be loaded before fading in
      Promise.all(preloadPromises).then(() => {
        setTimeout(() => {
          setIsTransitioning(false);
          setIsGalleryTransitioning(false);
          setContainerDimensions(null);
        }, 50);
      }).catch(() => {
        // Even if preload fails, fade in after a short delay
        setTimeout(() => {
          setIsTransitioning(false);
          setIsGalleryTransitioning(false);
          setContainerDimensions(null);
        }, 100);
      });
    }, 800);
  }, [isGalleryTransitioning, photos, preloadImage]);

  const closeGallery = useCallback(() => {
    if (isGalleryTransitioning) return;

    setIsGalleryTransitioning(true);

    // Wait for fade out to complete (800ms)
    setTimeout(() => {
      setShowGallery(false);

      // Preload current photo
      const currentPhoto = photos[currentIndex];
      if (currentPhoto) {
        preloadImage(currentPhoto.imageURL).then(() => {
          setTimeout(() => {
            setIsGalleryTransitioning(false);
          }, 50);
        }).catch(() => {
          setTimeout(() => {
            setIsGalleryTransitioning(false);
          }, 100);
        });
      }
    }, 800);
  }, [isGalleryTransitioning, photos, currentIndex, preloadImage]);

  const goToPhoto = useCallback((photoIndex: number) => {
    if (isGalleryTransitioning) return;

    setIsGalleryTransitioning(true);

    // Wait for fade out to complete (800ms)
    setTimeout(() => {
      setShowGallery(false);

      // Preload the target photo
      const targetPhoto = photos[photoIndex];
      if (targetPhoto) {
        preloadImage(targetPhoto.imageURL).then(() => {
          // Navigate to the photo
          if (photoIndex === 0) {
            navigate('/');
          } else {
            navigate(`/book/${photoIndex}`);
          }

          setTimeout(() => {
            setIsGalleryTransitioning(false);
          }, 50);
        }).catch(() => {
          // Navigate even if preload fails
          if (photoIndex === 0) {
            navigate('/');
          } else {
            navigate(`/book/${photoIndex}`);
          }

          setTimeout(() => {
            setIsGalleryTransitioning(false);
          }, 100);
        });
      }
    }, 800);
  }, [isGalleryTransitioning, navigate, photos, preloadImage]);

  const handlePortfolioClick = useCallback(() => {
    if (isTransitioning) return;

    // Capture current container dimensions
    if (photoContainerRef.current) {
      const { width, height } = photoContainerRef.current.getBoundingClientRect();
      setContainerDimensions({ width, height });
    }

    setIsTransitioning(true);

    // Preload the landing page image (index 0)
    const landingPhoto = photos[0];
    if (landingPhoto) {
      const preloadPromise = preloadImage(landingPhoto.imageURL);

      // Wait for fade out to complete (800ms)
      setTimeout(() => {
        // Navigate immediately
        navigate('/');

        // Wait for image to be loaded before fading in
        preloadPromise.then(() => {
          setTimeout(() => {
            setIsTransitioning(false);
            setContainerDimensions(null);
          }, 50);
        }).catch(() => {
          // Even if preload fails, fade in after a short delay
          setTimeout(() => {
            setIsTransitioning(false);
            setContainerDimensions(null);
          }, 100);
        });
      }, 800);
    }
  }, [isTransitioning, navigate, photos, preloadImage]);

  const formatDate = (date: Date | string): string => {
    const dateObj = typeof date === 'string' ? new (Date as any)(date) : date;
    return dateObj.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatMetaInfo = (date: Date | string, location: string): string => {
    const dateObj = typeof date === 'string' ? new (Date as any)(date) : date;
    const monthYear = dateObj.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
    });
    return `${monthYear} | ${location}`;
  };

  // Mouse movement handler for navigation visibility
  useEffect(() => {
    const handleMouseMove = () => {
      setShowNavigation(true);

      if (hideNavTimeout.current) {
        clearTimeout(hideNavTimeout.current);
      }

      hideNavTimeout.current = setTimeout(() => {
        // Don't hide if mouse is hovering over navigation
        if (!isHoveringNav) {
          setShowNavigation(false);
        }
      }, 2000);
    };

    window.addEventListener('mousemove', handleMouseMove);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      if (hideNavTimeout.current) {
        clearTimeout(hideNavTimeout.current);
      }
    };
  }, [isHoveringNav]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Disable keyboard navigation on mobile
      if (window.innerWidth <= 768) return;

      if (e.key === 'Escape' && isFullscreen) {
        exitFullscreen();
      } else if (e.key === 'ArrowLeft') {
        handlePrevious();
      } else if (e.key === 'ArrowRight') {
        handleNext();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [handlePrevious, handleNext, isFullscreen, exitFullscreen]);

  if (loading) {
    return <LoadingContainer>Loading photos...</LoadingContainer>;
  }

  if (error) {
    return <ErrorContainer>{error}</ErrorContainer>;
  }

  if (photos.length === 0) {
    return (
      <EmptyState>
        <h2>No Photos Yet</h2>
        <p>Start adding photos to your portfolio.</p>
      </EmptyState>
    );
  }

  const currentPhoto = photos[currentIndex];

  return (
    <>
      <MobileHomeButton
        onClick={handlePortfolioClick}
        disabled={isTransitioning}
        aria-label="Go to home"
      >
        ⌂
      </MobileHomeButton>

      <PageContainer>
        <BookContainer>
          <PhotoContainer
            ref={photoContainerRef}
            $isTransitioning={isTransitioning}
            $width={containerDimensions?.width}
            $height={containerDimensions?.height}
            onClick={enterFullscreen}
          >
              <StyledProtectedImage
                src={currentPhoto.imageURL}
                alt={currentPhoto.title}
                loading="eager"
                showWatermark={true}
                watermarkText={`© ${currentPhoto.title}`}
                crop={currentPhoto.crop}
              />
          </PhotoContainer>

        <TextContainer $isTransitioning={isTransitioning}>
          <Title>{currentPhoto.title}</Title>

          {currentPhoto.description && (
            <DescriptionContainer>
              {currentPhoto.description.split('\n').filter(para => para.trim()).map((para, index) => (
                <Description key={index}>{para}</Description>
              ))}
            </DescriptionContainer>
          )}

          {currentPhoto.date && currentPhoto.location && (
            <MetaInfo>{formatMetaInfo(currentPhoto.date, currentPhoto.location)}</MetaInfo>
          )}

          {/* {currentPhoto.cameraDetails && Object.keys(currentPhoto.cameraDetails).length > 0 && (
            <CameraSection>
              <CameraTitle>Camera Details</CameraTitle>
              <CameraGrid>
                {currentPhoto.cameraDetails.make && (
                  <CameraDetail>
                    <CameraLabel>Make</CameraLabel>
                    <CameraValue>{currentPhoto.cameraDetails.make}</CameraValue>
                  </CameraDetail>
                )}
                {currentPhoto.cameraDetails.model && (
                  <CameraDetail>
                    <CameraLabel>Model</CameraLabel>
                    <CameraValue>{currentPhoto.cameraDetails.model}</CameraValue>
                  </CameraDetail>
                )}
                {currentPhoto.cameraDetails.lens && (
                  <CameraDetail>
                    <CameraLabel>Lens</CameraLabel>
                    <CameraValue>{currentPhoto.cameraDetails.lens}</CameraValue>
                  </CameraDetail>
                )}
                {currentPhoto.cameraDetails.focalLength && (
                  <CameraDetail>
                    <CameraLabel>Focal Length</CameraLabel>
                    <CameraValue>{currentPhoto.cameraDetails.focalLength}</CameraValue>
                  </CameraDetail>
                )}
                {currentPhoto.cameraDetails.aperture && (
                  <CameraDetail>
                    <CameraLabel>Aperture</CameraLabel>
                    <CameraValue>{currentPhoto.cameraDetails.aperture}</CameraValue>
                  </CameraDetail>
                )}
                {currentPhoto.cameraDetails.shutterSpeed && (
                  <CameraDetail>
                    <CameraLabel>Shutter Speed</CameraLabel>
                    <CameraValue>{currentPhoto.cameraDetails.shutterSpeed}</CameraValue>
                  </CameraDetail>
                )}
                {currentPhoto.cameraDetails.iso && (
                  <CameraDetail>
                    <CameraLabel>ISO</CameraLabel>
                    <CameraValue>{currentPhoto.cameraDetails.iso}</CameraValue>
                  </CameraDetail>
                )}
              </CameraGrid>
            </CameraSection>
          )} */}
        </TextContainer>

        <NavigationContainer
          $visible={showNavigation}
          onMouseEnter={() => setIsHoveringNav(true)}
          onMouseLeave={() => setIsHoveringNav(false)}
        >
          <NavButton
            onClick={handlePortfolioClick}
            disabled={isTransitioning}
            aria-label="Go to home"
          >
            ⌂
          </NavButton>
          <GalleryButton
            onClick={openGallery}
            disabled={isTransitioning || isGalleryTransitioning}
            aria-label="Show all photos"
            style={{ marginBottom: '12px' }}
          >
            ⊞
          </GalleryButton>
          <NavButton
            onClick={handlePrevious}
            disabled={isTransitioning}
            aria-label="Previous photo"
          >
            ↑
          </NavButton>
          <PageIndicator>
            {currentIndex + 1} / {photos.length}
          </PageIndicator>
          <NavButton
            onClick={handleNext}
            disabled={currentIndex === photos.length - 1 || isTransitioning}
            aria-label="Next photo"
          >
            ↓
          </NavButton>
        </NavigationContainer>
      </BookContainer>
    </PageContainer>

    {isFullscreen && (
      <FullscreenOverlay ref={fullscreenRef} onClick={exitFullscreen}>
        <CloseButton onClick={exitFullscreen} aria-label="Close fullscreen">
          ✕
        </CloseButton>
        <FullscreenImageContainer
          $isZoomed={zoomLevel > 1}
          onClick={toggleZoom}
        >
          <FullscreenImageWrapper $zoomLevel={zoomLevel}>
            <FullscreenImage
              src={currentPhoto.imageURL}
              alt={currentPhoto.title}
              draggable={false}
              $zoomLevel={zoomLevel}
            />
          </FullscreenImageWrapper>
        </FullscreenImageContainer>
      </FullscreenOverlay>
    )}

    {showGallery && (
      <GalleryOverlay $isTransitioning={isGalleryTransitioning}>
        <CloseButton
          onClick={closeGallery}
          aria-label="Close gallery"
          disabled={isGalleryTransitioning}
          style={{
            cursor: isGalleryTransitioning ? 'not-allowed' : 'pointer',
            opacity: isGalleryTransitioning ? 0.5 : 1
          }}
        >
          ✕
        </CloseButton>
        <GalleryGrid>
          {photos.slice(1).map((photo, index) => (
            <GalleryImageContainer
              key={photo.id}
              onClick={() => goToPhoto(index + 1)}
              $disabled={isGalleryTransitioning}
            >
              <GalleryImage
                src={photo.imageURL}
                alt={photo.title}
                crop={photo.crop}
              />
            </GalleryImageContainer>
          ))}
        </GalleryGrid>
      </GalleryOverlay>
    )}
    </>
  );
};

export default PhotoBook;
